// apps/web/src/features/cargo/forms/useCargoImportUpload.ts
//
// Validate → presigned-PUT upload flow for Cargo import workbooks (#1232).
// Mirrors the lead-file / CP-photo pattern: validate the SOURCE file
// (extension/size/name) BEFORE any network call, then presign
// (`scope='cargo-import-file'`) → PUT → return the issued objectKey. The
// import is THEN processed via `POST /cargo/import` with the returned keys +
// chosen format (the binding step is the caller's, so the upload→process
// ordering is observable: every key resolves before the process call fires).
//
// @aiSecurity the extension/size/MIME gate mirrors the backend's
//   `cargo-import-file` scope policy (50 MiB, xlsx/xls). The server re-validates,
//   so this is a UX shortcut — a forged upload still 4xxs at the signed URL.

import { requestUploadUrl } from '../../../api/requests/index.js';

// 50 MiB ceiling — mirrors the backend `cargo-import-file` scope limit.
export const CARGO_IMPORT_MAX_BYTES = 50 * 1024 * 1024;
export const CARGO_IMPORT_NAME_MAX = 256;

// Excel workbooks only. The browser frequently reports an empty `file.type`
// for `.xls`, so the EXTENSION is authoritative for the pre-check; the wire
// MIME we send is derived from the extension.
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS_MIME = 'application/vnd.ms-excel';
export const CARGO_IMPORT_ACCEPT = '.xlsx,.xls';

export type CargoImportFileError = 'wrongExtension' | 'tooLarge' | 'nameTooLong';

export interface CargoImportValidation {
  ok: boolean;
  error?: CargoImportFileError;
}

function extensionMime(fileName: string): string | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.xlsx')) return XLSX_MIME;
  if (lower.endsWith('.xls')) return XLS_MIME;
  return null;
}

/** Pre-upload validation: extension, size, and filename length. */
export function validateCargoImportFile(file: File): CargoImportValidation {
  if (extensionMime(file.name) === null) return { ok: false, error: 'wrongExtension' };
  if (file.size > CARGO_IMPORT_MAX_BYTES) return { ok: false, error: 'tooLarge' };
  if (file.name.length > CARGO_IMPORT_NAME_MAX) return { ok: false, error: 'nameTooLong' };
  return { ok: true };
}

/**
 * Presign + PUT a single validated import file; resolves to the issued
 * objectKey. Throws on a presign/PUT failure so the caller can surface it and
 * abort the batch before the process call.
 */
export async function uploadCargoImportFile(file: File): Promise<string> {
  const contentType = extensionMime(file.name) ?? XLSX_MIME;
  const signed = await requestUploadUrl({
    contentType,
    contentLength: file.size,
    scope: 'cargo-import-file',
  });
  const put = await fetch(signed.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': contentType },
  });
  if (!put.ok) throw new Error(`upload failed: ${put.status}`);
  return signed.objectKey;
}
